import { FC, useCallback } from 'react'

import clsx from 'clsx'
import ImageAddLineIcon from 'remixicon-react/ImageAddLineIcon'

import { CustomSeoDetail, Image } from '@tribeplatform/gql-client/types'
import { useUploadFile } from '@tribeplatform/react-sdk/hooks'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { linkToFile } from '../../lib/upload'
import { PopoverImagePicker } from '../Picker/ImagePicker'

export const PostSeoDetails: FC<{
  seoDetail: CustomSeoDetail
  setSeoDetail
}> = ({ seoDetail, setSeoDetail }) => {
  const { loadingFile, uploadFile } = useUploadFile()
  const { title, description, thumbnail } = seoDetail || {}

  const thumbnailUrl = loadingFile?.url || (thumbnail as Image)?.url

  const onUploadFile = useCallback(
    async (file: File) => {
      uploadFile(file, async uploadedImage => {
        if (uploadedImage.id) {
          setSeoDetail('thumbnail', uploadedImage)
        }
      })
    },
    [uploadFile, setSeoDetail],
  )

  return (
    <div className="space-y-6 mt-3">
      <div>
        <h4>Search engine optimization</h4>
        <p className="text-sm text-basicSurface-300 mt-2">
          Search engine optimization (SEO) allows you to improve your ranking in
          search results. Use these features to make it easier for users to find
          your page when they search for it.{' '}
          {/* <Link variant="primary" href="#" target="_blank">
            Learn More
          </Link> */}
        </p>
      </div>
      <FormControl.Input
        name="title-meta"
        label="Meta title"
        helperText={
          <div className="flex justify-between text-basicSurface-300">
            <span>Recommended length: 60 characters</span>
            <span
              className={clsx(
                'text-bold',
                // eslint-disable-next-line no-nested-ternary
                title
                  ? title.length > 60
                    ? 'text-warning-800'
                    : 'text-success-800'
                  : '',
              )}
            >
              Length: {title?.length || 0}
            </span>
          </div>
        }
        placeholder="If empty, we will autogenerate this based on post title"
        value={title}
        onChange={e => setSeoDetail('title', e.target.value)}
      />
      <FormControl.Textarea
        name="description-meta"
        label="Meta description"
        placeholder="If empty, we will autogenerate this based on post content"
        value={description}
        onChange={e => setSeoDetail('description', e.target.value)}
        helperText={
          <div className="flex justify-between text-basicSurface-300">
            <span>Recommended length: 155 - 160 characters</span>
            <span
              className={clsx(
                'text-bold',
                // eslint-disable-next-line no-nested-ternary
                description
                  ? description.length > 160 || description.length < 155
                    ? 'text-warning-800'
                    : 'text-success-800'
                  : '',
              )}
            >
              Length: {description?.length || 0}
            </span>
          </div>
        }
      />

      <div className="mt-5">
        <h4>Social Image sharing</h4>
        <p className="text-sm text-basicSurface-300 mt-2">
          Social networks typically show your social sharing image together with
          your meta title and description. If you don’t add a social sharing
          image, we’ll use your community social sharing image or community logo
          instead.{' '}
          {/* <Link variant="primary" href="#" target="_blank">
            Learn More
          </Link> */}
        </p>
      </div>
      <div
        className="text-center mx-auto relative bg-cover bg-no-repeat bg-center border-t border-b sm:border h-[180px] sm:h-[261px]"
        style={{
          width: '500px',
          borderColor: '#dadde1',
          backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : '',
        }}
      >
        <div className="absolute top-0 bottom-0 left-0 right-0 cursor-pointer flex justify-center items-center">
          <PopoverImagePicker
            tabsProps={{
              file: {
                helper: (
                  <>
                    <p>Recommended size is 1200 x 630 pixels.</p>
                    <p>The file size limit is 10MB.</p>
                  </>
                ),
              },
            }}
            onRemove={() => {
              setSeoDetail('thumbnail', undefined)
            }}
            onLinkChange={async link => {
              const file = await linkToFile(link)
              if (file) onUploadFile(file)
            }}
            onImageChange={onUploadFile}
          >
            <div
              className={clsx(
                thumbnailUrl && 'bg-black opacity-70 rounded-md px-2 py-1',
              )}
            >
              {loadingFile ? (
                <SpinnerIcon className="w-9 h-9 animate-spin" />
              ) : (
                <>
                  <div className="mb-3 text-basicSurface-300 flex justify-center">
                    <ImageAddLineIcon className="w-9 h-9" />
                  </div>
                  <Link>Choose an image</Link>
                  <div className="text-basicSurface-300">
                    PNG, JPG, GIF up to 10MB
                  </div>
                </>
              )}
            </div>
          </PopoverImagePicker>
        </div>
      </div>
    </div>
  )
}
